import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack } from "@mui/material";

import { Agg } from "system/query/Aggs";
import { QueryField, QueryInfo } from "system/query/QueryInfo";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import ColumnManager, { densityOptions } from "components/tables/columns/ColumnManager";
import { ColumnDialogMode, ColumnDialogParams, ColumnModel, ColumnModelItem, ColumnRenderType, ColumnToolbarType } from "components/tables/columns/ColumnModel";
import { initialColumnModel } from "./DataTable";
import DataTableDialogContent from "./DataTableDialogContent";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest, WidgetRequestInit } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { dataTableDefaultSettings, DataTableSettingsStateType } from "./dataTableUtils";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import DataTableToolbar from "./DataTableToolbar";

export type DraftColumn = {
  id: string,
  label: string,
  operator?: ColumnModelItem["operator"],
  renderType?: ColumnModelItem["renderType"],
  activeColumnIndex?: number,
  showRenderTypeInSettings?: boolean,
};

const defaultWidgetRequest = {
  name: 'data-table',
  endpoint: 'query/agg',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultDataTableRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundKeywordfields = queryInfo?.fields?.filter((field: QueryField) => {
    return ["keyword", "keywords"].includes(field.type) && field.options?.length;
  }) || false;

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  if (foundKeywordfields?.length) {
    const foundKeywordField = foundKeywordfields.find((field: QueryField) => {
      return field.id === "sentiment";
    }) || foundKeywordfields[0];

    if (foundKeywordField && foundKeywordField.id && foundKeywordField.label) {
      const requestInitBody = {
        aggs: {
          [foundKeywordField.label]: {
            "op":"keyword",
            "field": foundKeywordField.id,
            "aggs": {
              "percent": {
                "op": "percent",
                "of_total": true
              }
            }
          }
        }
      };
      widgetRequest.requestInit = { body: requestInitBody };
      return [widgetRequest];
    }
  }
  return undefined;
}

// Extracts the first aggregation field from the widget and finds the corresponding field in the query info.
function getWidgetFields(requestInit?: WidgetRequestInit, queryInfo?: QueryInfo): QueryField | undefined {
  if (!requestInit || !requestInit.body || !queryInfo) {
    return undefined;
  }

  const requestBody = requestInit?.body || {};
  const widgetAggs = requestBody?.aggs ?? {};
  const keys = Object.keys(widgetAggs);

  if (keys.length === 0) {
    return undefined;
  }

  const label = keys[0];
  const agg = widgetAggs[label];
  const aggField = agg?.field;
  const field = queryInfo?.fields?.find((field: QueryField) => field.id === aggField);

  return field;
}

// Returns a unique field id for each column. DataGrid component requires unique ids.
export const getUniqueFieldId = (columns: ColumnModelItem[], stagedColumn: DraftColumn) => {
  let maxSuffix = 0;
  let baseIdName = "";

  if (stagedColumn.operator) {
    baseIdName = `${stagedColumn.id} (${stagedColumn.operator})`;
  } else {
    baseIdName = stagedColumn.id;
  }

  for (const { id } of columns) {
    if (id === baseIdName) {
      // If column item id & baseIdName match, get the max suffix between 1 and the current maxSuffix
      maxSuffix = Math.max(maxSuffix, 1);
    } else if (id.startsWith(`${baseIdName}-`)) {
      // If column with "-number" in it already exists, assign extracted number value to suffix variable
      // Converts string number -> ("1") to an actual number -> (1)
      const suffix = parseInt(id.split(`${baseIdName}-`)[1], 10);
      if (!isNaN(suffix)) {
        // If suffix variable is number, assign maxSuffix to incremented suffix variable
        maxSuffix = Math.max(maxSuffix, suffix + 1);
      }
    }
  }

  return maxSuffix === 0 ? baseIdName : `${baseIdName}-${maxSuffix}`;
};

export function submitDialog({
  columnDialogMode,
  columnModelState,
  draftColumn: stagedColumn,
  updateColumnModelState,
  i18n
}: {
  columnDialogMode?: ColumnDialogMode,
  columnModelState: ColumnModel,
  draftColumn: DraftColumn,
  updateColumnModelState: (newColumnModel: ColumnModel) => void,
  i18n: i18n
}) {
  const uniqueId = getUniqueFieldId(columnModelState.columns, stagedColumn);
  let newColumn = {} as ColumnModelItem;
  let newColumnModelState: { columns: ColumnModelItem[] } = { columns: [] };

  // If the draft/staged column has an operator, use it in the header name.
  let headerName = stagedColumn.operator ?
    `${stagedColumn.label} (${i18n.t(stagedColumn.operator)})` :
    stagedColumn.label ?? i18n.t("tables.no-label");

  const showRenderTypeInSettings = "showRenderTypeInSettings" in stagedColumn 
    ? stagedColumn.showRenderTypeInSettings 
    : undefined;

  newColumn = {
    id: uniqueId,
    field: uniqueId,
    fieldType: "integer",
    headerName,
    hasHeaderOptions: true,
    sourceField: stagedColumn.id,
    // These conditionals differentiate between original query fields & custom fields.
    // Custom fields won't have an operator, but will have a renderType.
    // They're used to display different types of column cells.
    ...(stagedColumn.operator && { operator: stagedColumn.operator }),
    ...(stagedColumn.renderType &&
      {
        renderType: stagedColumn.renderType,
      }
    ),
    ...(showRenderTypeInSettings !== undefined && {
      showRenderTypeInSettings: stagedColumn.showRenderTypeInSettings
    })
  };

  if (columnDialogMode === ColumnDialogMode.ADD_COLUMN) {
    newColumnModelState = {
      ...columnModelState,
      columns: [...columnModelState.columns, newColumn]
    };
  }

  if (columnDialogMode === ColumnDialogMode.UPDATE_COLUMN) {
    if (stagedColumn.activeColumnIndex !== null && stagedColumn.activeColumnIndex !== undefined) {
      const columnsCopy = [...columnModelState.columns];

      // Removes parenthesis & operator from field id if present
      // Ex: "comments (avg)" becomes "comments"
      const plainField = stagedColumn.label.split("(")[0].trim();

      if (stagedColumn.operator) {
        // If field is original query field, update headerName with new field plus operator
        newColumn.headerName = plainField + " " + `(${i18n.t(stagedColumn.operator)})`;
      }

      columnsCopy[stagedColumn.activeColumnIndex] = newColumn;
      newColumnModelState = { ...columnModelState, columns: columnsCopy };
    }
  }

  updateColumnModelState(newColumnModelState);
}

export default function DataTableSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'data-table');

  // Main agg (category) and sub aggs
  const widgetField = getWidgetFields(originalRequest?.requestInit, props.queryInfo);
  const [localCategory, setLocalCategory] = useState<QueryField | undefined>(widgetField);
  const handleSetLocalCategory = (category: QueryField) => setLocalCategory(category);

  // Settings state & update handler
  const [settingsState, setSettingsState] = useState<DataTableSettingsStateType>({...dataTableDefaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: DataTableSettingsStateType) => setSettingsState(updatedState);

  const [showRatioColumnAlert, setShowRatioColumnAlert] = useState<boolean>(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [columnDialogMode, setColumnDialogMode] = useState<ColumnDialogMode>();
  const [activeColumnIndex, setActiveColumnIndex] = useState<number>();
  const [draftColumn, setDraftColumn] = useState<DraftColumn | undefined>({
    id: "_count", label: i18n.t("records"), renderType: ColumnRenderType.Number
  });

  const handleSetDraftColumn = (draft: DraftColumn) => setDraftColumn(draft);
  const handleOpenDialog = () => setIsDialogOpen(true);
  function handleCloseDialog() {
    setIsDialogOpen(false);
    setDraftColumn(undefined);
  }

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const getAggsFromColumns = (columns: ColumnModelItem[]) => {
    return columns.reduce((acc: Agg[], curr: ColumnModelItem) => {
      if (curr.sourceField !== "_count") {
        if (curr.sourceField === "social_universe") {
          acc.push({ op: "social_universe" });
        } else {
          acc.push({ field: curr.sourceField, op: curr.operator });
        }
      }

      return acc;
    }, []);
  };

  const [localAggs, setLocalAggs] = useState<Agg[]>(props.widget?.data?.columnModel?.columns
    ? getAggsFromColumns(props.widget?.data?.columnModel?.columns) : []);

  const updateWidget = () => {
    if (!localCategory || !localAggs) { return; }

    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };
    let hasRatio = false;
    let hasPercentChange = false;

    const newAggs = localAggs.reduce((acc: any, agg: Agg, index: number) => {
      if (agg.op === "social_universe") {
        return {
          ...acc,
          // [`${socialUniverseField.label}`]: agg -- TODO: use (getSocialUniverseField()) once it's implemented
          ["social_universe"]: agg
        }
      }

      // If user added "ratio" column
      if (agg.field === "ratio") {
        hasRatio = true;
        return acc;
      }

      // If user added "percent change" column
      if (agg.field === "percent_change") {
        hasPercentChange = true;
        return acc;
      }

      // Skip if field or op is empty, ie; Records.
      if (!agg.field || !agg.op) {
        return acc;
      }

      // Using object keys to ensure unique entries (duplicates will be overwritten)
      return {
        ...acc,
        [`${agg.field} (${agg.op})`]: agg
      }
    }, {});

    requestInitBody.aggs = {
      [localCategory.label]: {
        field: localCategory.id,
        op: "keyword",
        aggs: {
          percent: {
            op: "percent"
          },
          ...newAggs
        }
      }
    };

    let requests:Array<WidgetRequest> = [];
    
    if (Object.keys(requestInitBody.aggs).length) {
      widgetRequest.requestInit = { body: requestInitBody };
      requests = [widgetRequest];
    }

    if (hasRatio) {
      let globalDataRequest: WidgetRequest = {
        name: "data-table-global",
        endpoint: `global-field-data/ds_gj4u3F40SLa/${localCategory.id}`,
      };
      requests = [...requests, globalDataRequest];
    }

    if (hasPercentChange) {
      const activeField = props.queryInfo?.fields?.find((f: QueryField) => f.id === localCategory.id);
      const activeFieldFilters = activeField?.options?.map(o => o.id);
      const activeFieldLabels = activeField?.options?.map(o => o.label);

      let percentChangeRequest: WidgetRequest = {
        name: "data-table-percent-change",
        endpoint: "query/agg",
        queryParams: {
          flatten: true,
          include_total: true
        },
        autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE,
        requestInit: {
          body: {
            aggs: {
              [localCategory.id]: {
                op: "histogram",
                field: "published",
                aggs: {
                  [localCategory.id]: {
                    op: "keyword",
                    field: localCategory.id,
                    filter: activeFieldFilters,
                    labels: activeFieldLabels
                  }
                }
              }
            }
          }
        }
      };
      requests = [...requests, percentChangeRequest];
    }

    props.saveWidgetDataAndRequests({
      columnModel: columnModelState,
      settings: settingsState
    }, requests).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  };

  // Column model state & update handlers
  const _initialColumnModel = {
    columns: initialColumnModel.columns.map((colItem: ColumnModelItem) => {
      return {
        ...colItem,
        headerName: colItem.headerName === "percent" ?
          i18n.t("percent-of-total") :
          i18n.t(colItem.headerName),
        hasHeaderOptions: true
      };
    }),
  };

  const [columnModelState, setColumnModelState] = useState<ColumnModel>(
    {
      ..._initialColumnModel,
      ...props.widget?.data?.columnModel
    }
  );

  function updateColumnModelState(newColumnModel: ColumnModel) {
    setLocalAggs(getAggsFromColumns(newColumnModel.columns));
    setColumnModelState(newColumnModel);
  }

  function handleColumnDialogMode(params: ColumnDialogParams) {
    if (params.mode === ColumnDialogMode.UPDATE_COLUMN && params.activeField) {
      const foundColumn = columnModelState.columns.find((col: ColumnModelItem) => col.id === params.activeField);

      if (foundColumn && foundColumn.sourceField) {
        const draftColumn = {
          id: foundColumn?.sourceField,
          label: foundColumn?.headerName ?? i18n.t("tables.no-label"),
          ...(foundColumn?.operator && {
            operator: foundColumn.operator
          }),
          ...(foundColumn?.renderType && {
            renderType: foundColumn.renderType
          }),
          activeColumnIndex: params.activeColumnIndex
        };

        setDraftColumn(draftColumn);
        setActiveColumnIndex(params.activeColumnIndex);
      }
    }

    setColumnDialogMode(params.mode);
    handleOpenDialog();
  }

  function handleDeleteColumn(columnItem: ColumnModelItem) {
    const indexOfColumnToRemove = columnModelState.columns.findIndex((col: ColumnModelItem) => col.field === columnItem.field);
    const newModelColumnsList = columnModelState.columns.toSpliced(indexOfColumnToRemove, 1);
    const newColumnModelState = { ...columnModelState, columns: newModelColumnsList };
    updateColumnModelState(newColumnModelState);
  }

  function deleteMultipleColumns(activeColumns: ColumnModelItem[]) {
    const updatedColumns = columnModelState.columns.filter((col: ColumnModelItem) => {
      return !activeColumns.includes(col);
    });

    const newColumnModelState = { ...columnModelState, columns: updatedColumns };
    updateColumnModelState(newColumnModelState);
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      maxWidth="md"
      open={true}
      tabs={[i18n.t("display"), i18n.t("columns")]}
      title={i18n.t("widget-types.data-table.settings.title")}
    >
      <>
        {tabValue === 0 &&
          <SplitSettingsWrapper>
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label="Custom Text"
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment}
                />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("graph")}>
                <SettingsToggleButton
                  keyid="density"
                  label={i18n.t("tables.row-density")}
                  optionsList={densityOptions}
                  value={settingsState.density} />
                <SettingsCheckbox
                  keyid="rowStripes"
                  label={i18n.t("tables.row-stripes")}
                  checked={settingsState.rowStripes}
                />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
        {tabValue === 1 &&
          <>
            <ColumnManager
              categoryLabel={localCategory?.label}
              columnModel={columnModelState}
              onColumnDialogMode={handleColumnDialogMode}
              onRemoveColumn={handleDeleteColumn}
              onRemoveMultipleColumns={deleteMultipleColumns}
              toolbarType={ColumnToolbarType.DATA_TABLE}
              updateColumnModel={updateColumnModelState}
              toolbar={props.queryInfo ?
                <DataTableToolbar
                  categoryLabel={localCategory?.label}
                  onColumnDialogMode={(params: ColumnDialogParams) => {
                    handleColumnDialogMode(params);
                  }}
                >
                  {showRatioColumnAlert &&
                    <Alert severity="warning" sx={{ marginTop: 1 }}>
                      {i18n.t("widget-types.data-table.settings.ratio-column-removed")}
                    </Alert>
                  }
                </DataTableToolbar> :
                <Alert severity="warning">{i18n.t("api-request.missing-query-error")}</Alert>
              }
            >
            </ColumnManager>
            <ResponsiveDialog open={isDialogOpen} maxWidth="sm">
              {columnDialogMode && <DialogTitle>{i18n.t(`tables.${columnDialogMode}`)}</DialogTitle>}
              <DialogContent>
                {props.queryInfo && (
                  <DataTableDialogContent
                    activeColumnIndex={activeColumnIndex}
                    columnDialogMode={columnDialogMode}
                    columnModelState={columnModelState}
                    draftColumn={draftColumn}
                    handleSetDraftColumn={handleSetDraftColumn}
                    handleSetLocalCategory={handleSetLocalCategory}
                    localCategory={localCategory}
                    queryInfo={props.queryInfo}
                    updateColumnModel={updateColumnModelState}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={handleCloseDialog}
                >
                  {i18n.t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => {
                    if (!props.queryInfo) {
                      return;
                    }
                    let showRatioAlert = false;
                    if (draftColumn && columnDialogMode !== ColumnDialogMode.UPDATE_CATEGORY) {
                      submitDialog({
                        columnDialogMode,
                        columnModelState,
                        draftColumn,
                        updateColumnModelState,
                        i18n
                      });
                    }
                    if (columnDialogMode === ColumnDialogMode.UPDATE_CATEGORY) {
                      // Check if base agg supports ratio - if no, remove ratio column & show alert.
                      if (!localCategory?.options) {
                        const ratioColumnIndex = columnModelState.columns.findIndex(col => col.id === "ratio");
  
                        if (ratioColumnIndex !== -1) {
                          const columnsWithoutRatio = columnModelState.columns.toSpliced(ratioColumnIndex, 1);
                          showRatioAlert = true;
                          updateColumnModelState({ ...columnModelState, columns: columnsWithoutRatio });
                        }
                      }
                    }
                    setShowRatioColumnAlert(showRatioAlert);
                    handleCloseDialog();
                  }}
                >
                  {columnDialogMode === "add-column" && i18n.t("add")}
                  {columnDialogMode === "update-column" && i18n.t("update")}
                  {columnDialogMode === "update-category" && i18n.t("update")}
                </Button>
              </DialogActions>
            </ResponsiveDialog>
          </>
        }
      </>
    </TabbedDialog>
  );
}
